import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

interface PendingReport {
  id: string
  order_id: string
  customer_email: string
  tier: string
  status: string
  created_at: string
  // Vetting information
  subject_name: string | null
  relationship_to_subject: string | null
  relationship_details: string | null
  attestation_accepted: boolean
  attestation_timestamp: string | null
  attestation_ip: string | null
}

const ADMIN_EMAILS = [
  'jstutzman@monadnockcyber.ai',
]

export default function Admin() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [reports, setReports] = useState<PendingReport[]>([])
  const [loading, setLoading] = useState(true)
  const [approving, setApproving] = useState<string | null>(null)
  const [message, setMessage] = useState('')

  const isAdmin = user && ADMIN_EMAILS.includes(user.email)

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard')
      return
    }

    fetchPendingReports()
  }, [isAdmin, navigate])

  async function fetchPendingReports() {
    try {
      const response = await api.get('/admin/reports/pending')
      setReports(response.data)
    } catch (error) {
      console.error('Failed to fetch pending reports:', error)
    } finally {
      setLoading(false)
    }
  }

  async function approveReport(reportId: string, sendEmail: boolean = true) {
    setApproving(reportId)
    setMessage('')

    try {
      const response = await api.post(`/admin/reports/${reportId}/approve`, {
        send_email: sendEmail
      })

      setMessage(`Report approved: ${response.data.message}`)

      // Remove from list
      setReports(reports.filter(r => r.id !== reportId))
    } catch (error: any) {
      setMessage(`Error: ${error.response?.data?.detail || 'Failed to approve report'}`)
    } finally {
      setApproving(null)
    }
  }

  const tierLabels: Record<string, string> = {
    quick: 'Exposure Report ($295)',
    full: 'Executive Intelligence ($2,500)',
    annual: 'Annual Monitoring ($1,500)',
  }

  const relationshipLabels: Record<string, string> = {
    self: 'Self-report',
    employer: 'Employer (with consent)',
    security_provider: 'Security Provider',
    family_authorized: 'Authorized Family',
    legal_representative: 'Legal Representative',
    other: 'Other',
  }

  const [selectedReport, setSelectedReport] = useState<PendingReport | null>(null)

  if (!isAdmin) {
    return null
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Admin: Report Approval</h1>
            <p className="text-dark-400">
              Review and approve reports before they're released to customers
            </p>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.startsWith('Error')
                ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                : 'bg-green-500/20 border border-green-500/50 text-green-400'
            }`}>
              {message}
            </div>
          )}

          <div className="card">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Pending Reports</h2>
              <button
                onClick={fetchPendingReports}
                className="text-sm text-ginger-500 hover:underline"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-ginger-500 mx-auto"></div>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">&#10003;</div>
                <h3 className="font-bold mb-2">All caught up!</h3>
                <p className="text-dark-400">
                  No reports pending review.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-700">
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Purchaser</th>
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Subject</th>
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Relationship</th>
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Tier</th>
                      <th className="text-left py-3 px-4 text-dark-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report) => (
                      <tr key={report.id} className="border-b border-dark-800">
                        <td className="py-3 px-4">
                          <div className="font-medium">{report.customer_email}</div>
                          <div className="text-xs text-dark-500">
                            {new Date(report.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium">{report.subject_name || 'Not provided'}</div>
                          <div className="text-xs text-dark-500">ID: {report.id.slice(0, 8)}...</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            report.relationship_to_subject === 'self'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {relationshipLabels[report.relationship_to_subject || ''] || report.relationship_to_subject || 'Unknown'}
                          </span>
                          {report.relationship_to_subject !== 'self' && (
                            <div className="text-xs text-yellow-400 mt-1">&#9888; Third-party request</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            report.tier === 'full' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {tierLabels[report.tier] || report.tier}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => setSelectedReport(report)}
                              className="text-sm text-ginger-500 hover:underline"
                            >
                              Details
                            </button>
                            <button
                              onClick={() => navigate(`/report/${report.id}`)}
                              className="text-sm text-blue-400 hover:underline"
                            >
                              View Report
                            </button>
                            <button
                              onClick={() => approveReport(report.id, true)}
                              disabled={approving === report.id}
                              className="btn btn-primary text-sm py-1 px-3 disabled:opacity-50"
                            >
                              {approving === report.id ? 'Releasing...' : 'Release & Email'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vetting Details Modal */}
            {selectedReport && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <div className="bg-dark-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                      <h3 className="text-xl font-bold">Order Vetting Details</h3>
                      <button
                        onClick={() => setSelectedReport(null)}
                        className="text-dark-400 hover:text-white text-2xl"
                      >
                        &times;
                      </button>
                    </div>

                    <div className="space-y-4">
                      {/* Purchaser Info */}
                      <div className="bg-dark-900 rounded-lg p-4">
                        <h4 className="font-semibold text-dark-300 mb-2">Purchaser</h4>
                        <p className="text-white">{selectedReport.customer_email}</p>
                      </div>

                      {/* Subject Info */}
                      <div className="bg-dark-900 rounded-lg p-4">
                        <h4 className="font-semibold text-dark-300 mb-2">Subject of Report</h4>
                        <p className="text-white text-lg">{selectedReport.subject_name || 'Not provided'}</p>
                      </div>

                      {/* Relationship */}
                      <div className={`rounded-lg p-4 ${
                        selectedReport.relationship_to_subject === 'self'
                          ? 'bg-green-500/10 border border-green-500/30'
                          : 'bg-yellow-500/10 border border-yellow-500/30'
                      }`}>
                        <h4 className="font-semibold text-dark-300 mb-2">Relationship to Subject</h4>
                        <p className={`text-lg ${
                          selectedReport.relationship_to_subject === 'self' ? 'text-green-400' : 'text-yellow-400'
                        }`}>
                          {relationshipLabels[selectedReport.relationship_to_subject || ''] || selectedReport.relationship_to_subject}
                        </p>
                        {selectedReport.relationship_details && (
                          <p className="text-dark-300 mt-2 text-sm">
                            <strong>Details:</strong> {selectedReport.relationship_details}
                          </p>
                        )}
                        {selectedReport.relationship_to_subject !== 'self' && (
                          <p className="text-yellow-400 mt-2 text-sm">
                            &#9888; This is a third-party request. Verify authorization before releasing.
                          </p>
                        )}
                      </div>

                      {/* Attestation */}
                      <div className="bg-dark-900 rounded-lg p-4">
                        <h4 className="font-semibold text-dark-300 mb-2">Legal Attestation</h4>
                        <div className="flex items-center gap-2 mb-2">
                          {selectedReport.attestation_accepted ? (
                            <>
                              <span className="text-green-400">&#10003;</span>
                              <span className="text-green-400">Attestation accepted</span>
                            </>
                          ) : (
                            <>
                              <span className="text-red-400">&#10007;</span>
                              <span className="text-red-400">Attestation NOT accepted</span>
                            </>
                          )}
                        </div>
                        <div className="text-sm text-dark-400 space-y-1">
                          <p><strong>Timestamp:</strong> {selectedReport.attestation_timestamp
                            ? new Date(selectedReport.attestation_timestamp).toLocaleString()
                            : 'N/A'}</p>
                          <p><strong>IP Address:</strong> {selectedReport.attestation_ip || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Vetting Decision */}
                      <div className="bg-dark-900 rounded-lg p-4">
                        <h4 className="font-semibold text-dark-300 mb-3">Vetting Checklist</h4>
                        <ul className="text-sm text-dark-300 space-y-2">
                          <li className="flex items-start gap-2">
                            <input type="checkbox" className="mt-1" />
                            <span>Purchaser email domain looks legitimate (not disposable/temp email)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <input type="checkbox" className="mt-1" />
                            <span>Relationship to subject is plausible</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <input type="checkbox" className="mt-1" />
                            <span>No red flags suggesting stalking or harassment intent</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <input type="checkbox" className="mt-1" />
                            <span>Report contents reviewed for accuracy</span>
                          </li>
                        </ul>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => {
                            approveReport(selectedReport.id, true)
                            setSelectedReport(null)
                          }}
                          disabled={approving === selectedReport.id}
                          className="btn btn-primary flex-1"
                        >
                          {approving === selectedReport.id ? 'Releasing...' : 'Release Report & Email Customer'}
                        </button>
                        <button
                          onClick={() => setSelectedReport(null)}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Admin Info */}
          <div className="mt-8 card bg-dark-800/50">
            <h3 className="font-bold mb-4">Vetting Workflow</h3>
            <ul className="text-dark-400 text-sm space-y-2">
              <li><strong>&#8226; MANDATORY:</strong> All paid reports require manual vetting before release</li>
              <li>&#8226; Click "Details" to review purchaser authorization and attestation</li>
              <li>&#8226; Third-party requests (non-self) require extra scrutiny</li>
              <li>&#8226; Customer sees "Processing" until you release</li>
              <li>&#8226; All vetting decisions are logged with admin email and timestamp</li>
            </ul>

            <h4 className="font-semibold mt-6 mb-2 text-yellow-400">Red Flags to Watch For:</h4>
            <ul className="text-dark-400 text-sm space-y-2">
              <li>&#8226; Disposable/temporary email addresses (guerrillamail, 10minutemail, etc.)</li>
              <li>&#8226; Third-party requests with vague relationship descriptions</li>
              <li>&#8226; Purchaser and subject in different geographic regions with no explanation</li>
              <li>&#8226; Multiple requests for different people from same account</li>
              <li>&#8226; Request contains notes suggesting harassment or threats</li>
            </ul>

            <p className="mt-4 text-sm text-red-400">
              <strong>If in doubt, DO NOT release.</strong> Contact the purchaser for verification or decline the order.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
